import { ReactElement, useCallback, useMemo, useState } from 'react'
import debounce from 'debounce-promise'
import { useTranslation } from 'react-i18next'
import { IconMapMarker, IconAdd } from '@machobear/design-system'
import { pipe, concat, path, prop, map, values, pick, partialRight, ifElse, __ } from 'ramda'
import { createSelector } from 'reselect'
import { searchBuilding } from '@/ports'
import { AutoSuggestFieldInput } from '@/components/ds-form/auto-suggest-field'
import { mergeSpec, headObj, isTruthy } from '@/utils'

const parseCountryCode = (value: string) => {
  switch (value) {
    case 'CAN':
      return 'CA'
    case 'USA':
      return 'US'
    default:
      return value
  }
}

const buildingSelector = createSelector(
  prop('buildings'),
  prop('extra_addresses'),
  path(['files', 'building']),
  (buildings, extras, files) =>
    pipe(
      values,
      concat(__, extras),
      map(
        pipe(
          pick([
            'id',
            'name',
            'street_name',
            'street_number',
            'street_address',
            'city',
            'state',
            'zip',
            'country_code',
            'type',
            'street_name',
            'full_street_name',
            'lat',
            'lon'
          ]),
          mergeSpec({
            name: ifElse(pipe(prop('name'), isTruthy), prop('name'), prop('street_name')),
            image: pipe(prop('id'), partialRight(prop, [files]), headObj),
            value: prop('full_street_name'),
            label: prop('full_street_name'),
            lat: prop('lat'),
            // BE returns diff longitude abbreviation so we need to map it:
            lng: prop('lon'),
            // Buildings in "extra_addresses" use a 3 character country_code, so we need to parse:
            country_code: pipe(prop('country_code'), parseCountryCode)
          })
        )
      )
    )(buildings)
)

interface Suggestion {
  icon: ReactElement
  text: string
  description?: string
  value: any
}

interface BuildingSearchProps {
  enterAddressManually: boolean
  setEnterAddressManually: (value: boolean) => void
  values: any
  setValues: any
}

const BuildingSearchField = ({
  enterAddressManually,
  setEnterAddressManually,
  values,
  setValues
}: BuildingSearchProps) => {
  const { t } = useTranslation()
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const disableSearchField = !values.type || !values.scope

  const generateBuildingSuggestions = useCallback(
    async (inputText: string, buildingType: string) => {
      if (inputText === '') {
        setSuggestions([])
        return
      }
      const response = await searchBuilding({
        body: {
          address: inputText,
          building_type: buildingType || 'condo',
          country_code: ['ca', 'us']
        }
      })

      if (response?.response.ok) {
        const buildings: any = buildingSelector(response.body)
        const buildingSuggestions = buildings.map((building: any) => ({
          icon: <IconMapMarker />,
          text: building?.name,
          description: `${building?.full_street_name}, ${building?.city}, ${building?.state}, ${building?.zip}`,
          value: {
            text: building?.full_street_name,
            id: building.id,
            building_name: building.name,
            full_address: building.full_street_name,
            street_number: building.street_number,
            city: building.city,
            province: building.state,
            zip: building.zip,
            country: (building.country_code || 'ca').toLowerCase(),
            building,
            isExistingBuilding: true
          }
        }))

        const manualEntry = {
          icon: <IconAdd />,
          text: t('Add address manually'),
          value: {
            text: '',
            id: null,
            building_name: '',
            full_address: inputText,
            street_number: '',
            city: '',
            province: '',
            zip: '',
            country: '',
            building: {},
            isExistingBuilding: false
          }
        }

        setSuggestions([...buildingSuggestions, manualEntry])
      }
    },
    [t]
  )

  const debouncedGenerateBuildingSuggestions = useMemo(() => {
    return debounce(generateBuildingSuggestions, 500)
  }, [generateBuildingSuggestions])

  const handleAddressChange = async (input: { text: string; value?: any }) => {
    if (input.value) {
      // Handle user selection:
      const { value } = input
      if (value.isExistingBuilding) {
        setEnterAddressManually(false)
        setValues({
          ...values,
          full_address: input,
          building_id: value.id || null,
          building_name: value.building_name || '',
          address: value.full_address || '',
          street_number: value.street_number,
          city: value.city || '',
          province: value?.province || '',
          zip: value.zip || '',
          country: value.country || 'ca',
          building: value.building
        })
      } else {
        setEnterAddressManually(true)
        setValues({
          ...values,
          full_address: { text: '', value: '', isOptional: true },
          building_id: null,
          building_name: '',
          address: value.full_address || '',
          street_number: '',
          city: '',
          province: '',
          zip: '',
          country: 'ca',
          building: {}
        })
      }
      setSuggestions([])
      return
    }
    // Handle user typing in address field:
    debouncedGenerateBuildingSuggestions(input.text, values.type)
  }

  const handleClear = () => {
    setSuggestions([])
    setEnterAddressManually(false)
    setValues({
      ...values,
      full_address: { text: '', value: '' },
      building_id: null,
      building_name: '',
      address: '',
      street_number: '',
      city: '',
      province: '',
      zip: '',
      country: 'ca',
      building: {}
    })
  }

  return (
    <AutoSuggestFieldInput
      scrollToTopOnMobile={false}
      type="search"
      id="full_address"
      name="full_address"
      label={t('l.listing.create.full_address.label', 'Full address')}
      suggestions={suggestions}
      value={{ text: values.full_address.text }}
      onChange={handleAddressChange}
      onClear={handleClear}
      required={!enterAddressManually}
      automaticSelection={false}
      hideSuggestionsOnSelection
      disabled={disableSearchField}
    />
  )
}

export default BuildingSearchField
